/**
 * VerifyEmailPage - Handles email verification token verification.
 *
 * Users land here from the verification email after signup. The page verifies
 * the token via fetch to the API (which sets session cookies with
 * autoSignInAfterVerification), then redirects to /v2.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { betterAuthVerifyEmail } from '../../services/auth';
import { logger } from '../../../utils/logger';
import voicebrainLogo from '../../../assets/images/voicebrain-logo.svg';
import loginBackground from '../../../assets/images/login-background.jpg';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME || 'VoiceBrain';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid verification link — no token provided.');
      return;
    }

    let cancelled = false;

    async function verify() {
      const result = await betterAuthVerifyEmail(token!);
      if (cancelled) return;

      if (result.error) {
        logger.error('[VerifyEmailPage] Verification failed:', result.error);
        setError(result.error);
        return;
      }

      logger.info('[VerifyEmailPage] Email verified, redirecting to dashboard');
      navigate('/v2', { replace: true });
    }

    verify();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
        <div className="w-full max-w-sm relative z-10">
          <div className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8 text-center">
            <div className="w-16 h-16 bg-vb-danger/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <HiExclamationTriangle className="w-8 h-8 text-vb-danger" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
              Verification failed
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
              {error}
            </p>
            <Link
              to="/v2/login"
              className="inline-flex items-center gap-2 text-sm text-vb-primary hover:text-vb-primary-hover transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading state while verifying
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
      <div className="w-full max-w-sm relative z-10">
        <div className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8 text-center">
          <img
            src={voicebrainLogo}
            alt={BRAND_NAME}
            className="h-10 mx-auto mb-4 invert dark:invert-0"
          />
          <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
            Verifying your email...
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
